/**
 * Timezone helpers.
 *
 * Showings are entered as a plain date and time ("Aug 22, 2:00 PM") — which is
 * only a real moment once you know whose 2 PM it is. Every agent carries an
 * IANA zone, and their sellers' showing reminders fire against it.
 */

export const DEFAULT_TIMEZONE = "America/Denver";

/** The browser's own zone, or the default if the browser won't say. */
export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/** US zones first — that's the market — then a short tail of common others. */
export const TIMEZONE_OPTIONS = [
  { id: "America/New_York", label: "Eastern — New York" },
  { id: "America/Chicago", label: "Central — Chicago" },
  { id: "America/Denver", label: "Mountain — Denver" },
  { id: "America/Phoenix", label: "Mountain, no DST — Phoenix" },
  { id: "America/Los_Angeles", label: "Pacific — Los Angeles" },
  { id: "America/Anchorage", label: "Alaska — Anchorage" },
  { id: "Pacific/Honolulu", label: "Hawaii — Honolulu" },
  { id: "America/Toronto", label: "Eastern — Toronto" },
  { id: "America/Vancouver", label: "Pacific — Vancouver" },
];

/**
 * The option list, guaranteed to contain `current`.
 *
 * An agent may sit in a zone we didn't list (or one added to the IANA database
 * after this file was written). Dropping it from the list would silently reset
 * them to Mountain the next time they saved the form, so we append it instead.
 */
export function timezoneOptions(current) {
  const tz = (current || "").trim();
  if (!tz || TIMEZONE_OPTIONS.some((o) => o.id === tz)) return TIMEZONE_OPTIONS;
  return [...TIMEZONE_OPTIONS, { id: tz, label: tz.replace(/_/g, " ") }];
}

/** "2:30 PM MDT" for a Date, rendered in the given zone. */
export function formatInZone(date, tz, opts = {}) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz || DEFAULT_TIMEZONE,
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
      ...opts,
    }).format(date);
  } catch {
    return "";
  }
}
